import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState, useEffect } from 'react';
import {
  Text, Spinner, Badge, Button, Field, Input, Select,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, CartesianGrid, AreaChart, Area,
} from 'recharts';
import {
  fetchFinanceSummary, fetchProtocolsSummary,
  fetchFinancePL, fetchCashflow, fetchBudgetBreakdown,
  createFinanceBudget, type CreateBudgetInput,
  fetchLoteBudget, updateCommittedAmount,
} from '../api/dashboard.api';
import { fetchRanchos } from '../api/rancho.api';
import { useResetOnOpen } from '../hooks/useResetOnOpen';
import { useEntityNames } from '../hooks/useEntityNames';
import { CycleCombobox } from '../components/shared/CycleCombobox';
import { useRanchoLotes } from '../hooks/useRanchoLotes';
import { useAuthStore } from '../store/auth.store';
import { useRanchoStore } from '../store/rancho.store';
import { TelegramHelpButton } from '../components/shared/TelegramHelpButton';
import { useT } from '../i18n';
import { NumberInput } from '../components/shared/NumberInput';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import type { CostSummary, Protocol, BudgetBreakdownRow } from '../types/api.types';
import { cropLabel } from '../utils/crop-label';
import { PageTitle } from '../components/shared/PageTitle';
import { DataTable } from '../components/shared/DataTable';
import { SubTabs } from '../components/shared/SubTabs';

// ── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root:   { display: 'flex', flexDirection: 'column', gap: '24px' },
  grid:   { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '24px' },
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '16px' },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '20px',
    display: 'flex', flexDirection: 'column', gap: '12px',
  },
  kpiCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '16px', display: 'flex', flexDirection: 'column', gap: '4px',
  },
  kpiLabel: { fontSize: '12px', color: tokens.colorNeutralForeground3 },
  kpiValue: { fontSize: '28px', fontWeight: '700', fontVariantNumeric: 'tabular-nums' },
  total:    { fontSize: '32px', fontWeight: '700' },
  sub:      { color: tokens.colorNeutralForeground3, fontSize: '13px' },
  protocolRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 80px 110px 110px 120px 80px',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: '13px', gap: '8px',
  },
  budgetRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 120px 120px 120px 100px',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: '13px', gap: '8px',
  },
  protocolHeader: {
    color: tokens.colorNeutralForeground3, fontWeight: '600',
    fontSize: '11px', textTransform: 'uppercase',
  },
  cost:            { textAlign: 'right' as const, fontWeight: '600', fontVariantNumeric: 'tabular-nums' },
  positive:        { color: tokens.colorPaletteGreenForeground1, fontVariantNumeric: 'tabular-nums' },
  negative:        { color: tokens.colorPaletteRedForeground1,   fontVariantNumeric: 'tabular-nums' },
  granularityRow:  { display: 'flex', alignItems: 'center', gap: '8px' },
});

// ── Constants ────────────────────────────────────────────────────────────────

// ── NewBudgetDialog ────────────────────────────────────────────────────────────

function NewBudgetDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t  = useT();
  const f  = t.finance;
  const qc = useQueryClient();

  const { data: ranchos = [] } = useQuery({ queryKey: ['ranchos'], queryFn: () => fetchRanchos() });
  const activeRanchoId = useRanchoStore((st) => st.activeRanchoId);
  const [ranchoId, setRanchoId] = useState(activeRanchoId ?? '');
  const [loteId, setLoteId] = useState('');
  // #59: el rancho del diálogo arranca en el activo del TopBar y los lotes se filtran por él.
  const { lotes } = useRanchoLotes(true, { value: loteId, reset: () => setLoteId('') }, ranchoId);
  const ranchoOptions = ranchos.map(r => ({ id: r.ranchoId, label: r.name }));
  const loteOptions = lotes.map(l => ({ id: l.loteId, label: l.name }));
  const [ciclo, setCiclo] = useState('');
  const [totalBudget, setTotalBudget] = useState('');
  const [estimatedYield, setEstimatedYield] = useState('');
  const [estimatedPrice, setEstimatedPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const fe = useFieldErrors();
  // B2-5: el catálogo de monedas viene de la misma consulta que el resumen de presupuesto.
  const { data: currencyCatalog } = useQuery({
    queryKey: ['finance-budget-breakdown', activeRanchoId],
    queryFn:  () => fetchBudgetBreakdown(undefined, activeRanchoId),
    enabled:  open,
  });
  const [currency, setCurrency] = useState('');
  const currencyOptions = currencyCatalog?.currencies ?? [];
  const baseCurrency = currencyCatalog?.baseCurrency ?? '';

  const reset = () => {
    setRanchoId(activeRanchoId ?? ''); setLoteId(''); setCiclo(''); setTotalBudget('');
    setEstimatedYield(''); setEstimatedPrice(''); setNotes(''); setError(''); setCurrency(''); fe.clear();
  };
  useResetOnOpen(open, reset);

  const mut = useMutation({
    mutationFn: () => {
      const input: CreateBudgetInput = {
        ranchoId: ranchoId.trim(), loteId: loteId.trim(), ciclo: ciclo.trim(),
        totalBudget: parseFloat(totalBudget) || 0,
        estimatedYield: estimatedYield ? parseFloat(estimatedYield) : 0,
        estimatedPrice: estimatedPrice ? parseFloat(estimatedPrice) : 0,
        notes: notes.trim() || undefined,
        currency: currency && currency !== baseCurrency ? currency : undefined,
      };
      return createFinanceBudget(input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance-budget-breakdown'] });
      reset();
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? f.createBudgetError),
  });

  const handleSubmit = () => {
    setError('');
    const ok = fe.validate({
      ranchoId: !ranchoId.trim() && f.ranchoRequired,
      loteId: !loteId.trim() && f.loteRequired,
      ciclo: !ciclo.trim() && f.cicloRequired,
      totalBudget: (!totalBudget || parseFloat(totalBudget) <= 0) && f.totalBudgetRequired,
    });
    if (!ok) return;
    mut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) { reset(); onClose(); } }}>
      <DialogSurface style={{ maxWidth: '440px' }}>
        <DialogBody>
          <DialogTitle action={
            <TelegramHelpButton
              topic="finance"
              voiceExample={t.telegramHelp.financeVoice}
              voiceDesc={t.telegramHelp.financeDesc}
            />
          }>{f.newBudgetTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <SearchableSelect
                  label={f.fieldRanchoId}
                  placeholder={f.searchRanchoPlaceholder}
                  noResultsText={t.common.searchNoResults}
                  options={ranchoOptions}
                  value={ranchoId}
                  onChange={(id) => { setRanchoId(id); setLoteId(''); fe.clear('ranchoId'); }}
                  error={fe.errors.ranchoId}
                  disabled={mut.isPending}
                />
                <SearchableSelect
                  label={f.fieldLoteId}
                  placeholder={f.searchLotePlaceholder}
                  noResultsText={t.common.searchNoResults}
                  options={loteOptions}
                  value={loteId}
                  onChange={(id) => { setLoteId(id); fe.clear('loteId'); }}
                  error={fe.errors.loteId}
                  disabled={mut.isPending}
                />
              </div>
              <Field label={f.fieldCiclo} {...fe.props('ciclo')}>
                <CycleCombobox loteId={loteId} value={ciclo} onChange={(v) => { setCiclo(v); fe.clear('ciclo'); }} disabled={mut.isPending} />
              </Field>
              <Field label={f.fieldTotalBudget} {...fe.props('totalBudget')}>
                <NumberInput value={totalBudget} onValueChange={(v) => { setTotalBudget(v); fe.clear('totalBudget'); }} disabled={mut.isPending} />
              </Field>
              {currencyOptions.length > 0 && (
                <Field label={f.fieldCurrency}>
                  <Select value={currency || baseCurrency} onChange={(_, d) => setCurrency(d.value)} disabled={mut.isPending}>
                    {currencyOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </Field>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={f.fieldEstimatedYield}>
                  <NumberInput value={estimatedYield} onValueChange={(v) => setEstimatedYield(v)} disabled={mut.isPending} />
                </Field>
                <Field label={f.fieldEstimatedPrice}>
                  <NumberInput value={estimatedPrice} onValueChange={(v) => setEstimatedPrice(v)} disabled={mut.isPending} />
                </Field>
              </div>
              <Field label={f.fieldNotes}>
                <Input value={notes} onChange={(_, d) => setNotes(d.value)} disabled={mut.isPending} />
              </Field>
              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { reset(); onClose(); }} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {f.create}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ── EditCommittedDialog ────────────────────────────────────────────────────────

function EditCommittedDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t  = useT();
  const f  = t.finance;
  const qc = useQueryClient();

  const [loteId, setLoteId] = useState('');
  const { lotes } = useRanchoLotes(true, { value: loteId, reset: () => setLoteId('') });
  const loteOptions = lotes.map(l => ({ id: l.loteId, label: l.name }));
  const [committedAmount, setCommittedAmount] = useState('');
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  const { data: loteBudgets = [], isLoading: loadingLoteBudgets } = useQuery({
    queryKey: ['lote-budget', loteId],
    queryFn:  () => fetchLoteBudget(loteId),
    enabled:  !!loteId,
  });

  const activeBudget = loteBudgets.find(b => b.status === 'ACTIVE');

  useEffect(() => {
    if (activeBudget) setCommittedAmount(String(activeBudget.committedAmount ?? 0));
  }, [activeBudget?.budgetId]);

  const reset = () => { setLoteId(''); setCommittedAmount(''); setError(''); fe.clear(); };

  const mut = useMutation({
    mutationFn: () => {
      if (!activeBudget) throw new Error(f.noActiveBudget);
      return updateCommittedAmount(activeBudget.budgetId, loteId, parseFloat(committedAmount) || 0);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance-budget-breakdown'] });
      qc.invalidateQueries({ queryKey: ['lote-budget', loteId] });
      reset();
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? e?.message ?? f.createBudgetError),
  });

  const handleSubmit = () => {
    setError('');
    const ok = fe.validate({
      loteId: !loteId.trim() && f.loteRequired,
      committed: activeBudget && (committedAmount === '' || parseFloat(committedAmount) < 0) && f.totalBudgetRequired,
    });
    if (!ok) return;
    if (!activeBudget) return setError(f.noActiveBudget);
    mut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) { reset(); onClose(); } }}>
      <DialogSurface style={{ maxWidth: '400px' }}>
        <DialogBody>
          <DialogTitle>{f.editCommittedTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <SearchableSelect
                label={f.fieldLoteId}
                placeholder={f.searchLotePlaceholder}
                noResultsText={t.common.searchNoResults}
                options={loteOptions}
                value={loteId}
                onChange={(id) => { setLoteId(id); setCommittedAmount(''); setError(''); fe.clear(); }}
                error={fe.errors.loteId}
                disabled={mut.isPending}
              />
              {loteId && loadingLoteBudgets && <Spinner size="tiny" label={f.budgetLoading} />}
              {loteId && !loadingLoteBudgets && !activeBudget && (
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{f.noActiveBudget}</Text>
              )}
              {activeBudget && (
                <Field label={f.editCommittedLabel} {...fe.props('committed')}>
                  <NumberInput
                    value={committedAmount}
                    onValueChange={(v) => { setCommittedAmount(v); fe.clear('committed'); }}
                    disabled={mut.isPending}
                  />
                </Field>
              )}
              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { reset(); onClose(); }} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={mut.isPending || !activeBudget}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {f.editCommittedSave}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export function FinancePage() {
  const s = useStyles();
  const t = useT();
  const f = t.finance;
  const { user } = useAuthStore();
  const isAdmin = user?.roles.some(r => r === 'ADMIN' || r === 'SUPER_ADMIN') ?? false;
  const { formatCurrency, formatDate } = useLocaleSettings();
  const { loteName } = useEntityNames();

  const [tab, setTab]                       = useState<string>('resumen');
  const [cashGranularity, setCashGranularity] = useState<'week' | 'month'>('week');
  const [newBudgetOpen, setNewBudgetOpen]   = useState(false);
  const [editCommittedOpen, setEditCommittedOpen] = useState(false);

  const { activeRanchoId } = useRanchoStore();

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: summary, isLoading } = useQuery({
    queryKey: ['finance', activeRanchoId],
    queryFn:  () => fetchFinanceSummary(activeRanchoId),
  });

  const { data: protocols = [], isLoading: loadingProto } = useQuery({
    queryKey: ['protocols'],
    queryFn:  fetchProtocolsSummary,
  });

  // P&L y flujo de caja filtran por el rancho activo. Sin rancho activo
  // (activeRanchoId null/undefined) el fetcher omite ranchoId y el backend
  // devuelve la empresa completa.
  const { data: pl, isLoading: loadingPL } = useQuery({
    queryKey: ['finance-pl', activeRanchoId],
    queryFn:  () => fetchFinancePL({ ranchoId: activeRanchoId, from: '2000-01-01' }),
    enabled:  tab === 'pl',
  });

  const { data: cashflow, isLoading: loadingCashflow } = useQuery({
    queryKey: ['finance-cashflow', cashGranularity, activeRanchoId],
    queryFn:  () => fetchCashflow({ granularity: cashGranularity, ranchoId: activeRanchoId }),
    enabled:  tab === 'cashflow',
  });

  const { data: budgetBreakdown, isLoading: loadingBudget } = useQuery({
    queryKey: ['finance-budget-breakdown', activeRanchoId],
    queryFn:  () => fetchBudgetBreakdown(undefined, activeRanchoId),
    enabled:  tab === 'presupuesto',
  });

  if (isLoading) return <Spinner size="small" label={f.loadingSummary} />;
  if (!summary)  return null;

  // ── Helpers ────────────────────────────────────────────────────────────────

  const fmt    = (n: number) => formatCurrency(n);
  const fmtPct = (n: number | null) => n == null ? '—' : `${n.toFixed(1)}%`;

  const byLoteChart = summary.byLote.map((l) => ({ loteId: loteName(l.loteId, (l as { loteName?: string }).loteName), total: l.totalCost }));
  const trendChart  = summary.trend.map((tr) => ({ week: tr.week, total: tr.cost }));

  const typeLabel: Record<string, string> = {
    QUIMICO:      f.protocolTypeQuimico,
    FERTILIZANTE: f.protocolTypeFertilizante,
    AMBOS:        f.protocolTypeAmbos,
  };

  const sourceLabel: Record<string, string> = {
    TELEGRAM_DOC:   f.protocolSourceDoc,
    TELEGRAM_PHOTO: f.protocolSourcePhoto,
    TELEGRAM_TEXT:  f.protocolSourceText,
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={s.root}>
      <PageTitle>{f.title}</PageTitle>

      {/* Tab navigation */}
      <SubTabs selected={tab} onSelect={setTab} tabs={[{ value: 'resumen', label: f.tabResumen }, { value: 'pl', label: f.tabPL }, { value: 'cashflow', label: f.tabCashflow }, { value: 'presupuesto', label: f.tabPresupuesto }]} />

      {/* ── TAB: RESUMEN ────────────────────────────────────────────────────── */}
      {tab === 'resumen' && (
        <>
          {/* KPI total */}
          <div className={s.card} style={{ maxWidth: '260px' }}>
            <div className={s.sub}>{f.totalCost}</div>
            <div className={s.total}>{formatCurrency(summary.totalCost)}</div>
          </div>

          {/* Charts */}
          <div className={s.grid}>
            <div className={s.card}>
              <Text weight="semibold">{f.costByLot}</Text>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={byLoteChart} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                  <XAxis dataKey="loteId" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Bar dataKey="total" fill={tokens.colorBrandBackground} name={f.costLabel} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className={s.card}>
              <Text weight="semibold">{f.weeklyTrend}</Text>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trendChart} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="total" stroke={tokens.colorBrandBackground} name={f.costLabel} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Tabla por lote */}
          <div className={s.card}>
            <Text weight="semibold">{f.byLotTable}</Text>
            <DataTable<CostSummary & { loteName: string }>
              rows={summary.byLote}
              rowKey={(row) => row.loteId}
              columns={[
                { key: 'lot', header: f.tableLot, width: 2, render: (row) => loteName(row.loteId, row.loteName) },
                { key: 'acts', header: f.tableActivities, align: 'right', render: (row) => row.activityCount },
                { key: 'total', header: f.tableTotal, align: 'right', render: (row) => <span style={{ fontWeight: 600 }}>{formatCurrency(row.totalCost)}</span> },
              ]}
            />
          </div>

          {/* Protocolos */}
          <div className={s.card}>
            <Text weight="semibold">{f.protocolsTitle}</Text>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              {f.protocolsDesc}
            </Text>

            {loadingProto && <Spinner size="tiny" label={f.protocolsLoading} />}

            {!loadingProto && protocols.length === 0 && (
              <Text size={200} style={{ color: tokens.colorNeutralForeground3, fontStyle: 'italic' }}>
                {f.protocolsEmpty}
              </Text>
            )}

            {!loadingProto && protocols.length > 0 && (
              <>
                <div className={`${s.protocolRow} ${s.protocolHeader}`}>
                  <span>{f.protocolCropCycle}</span>
                  <span>{f.protocolType}</span>
                  <span style={{ textAlign: 'right' }}>{f.protocolChemHa}</span>
                  <span style={{ textAlign: 'right' }}>{f.protocolFertHa}</span>
                  <span style={{ textAlign: 'right' }}>{f.protocolTotalHa}</span>
                  <span style={{ textAlign: 'right' }}>{f.protocolOrigin}</span>
                </div>
                {protocols.map((p: Protocol) => (
                  <div key={p.protocolId} className={s.protocolRow}>
                    <div>
                      <div style={{ fontWeight: '500' }}>{p.crop ? cropLabel(p.crop) : '—'}</div>
                      <div style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
                        {p.ciclo ?? '—'} &middot; {formatDate(p.createdAt.slice(0, 10))}
                      </div>
                    </div>
                    <Badge
                      appearance="tint"
                      color={p.type === 'QUIMICO' ? 'warning' : p.type === 'FERTILIZANTE' ? 'success' : 'brand'}
                    >
                      {typeLabel[p.type] ?? p.type}
                    </Badge>
                    <div className={s.cost}>{fmt(p.costoQuimicoHa)}</div>
                    <div className={s.cost}>{fmt(p.costoFertilizanteHa)}</div>
                    <div className={s.cost} style={{ color: tokens.colorBrandForeground1 }}>
                      {fmt(p.costoTotalHa)}
                    </div>
                    <div style={{ textAlign: 'right', fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
                      {sourceLabel[p.source] ?? p.source}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      )}

      {/* ── TAB: P&L ────────────────────────────────────────────────────────── */}
      {tab === 'pl' && (
        <>
          {loadingPL && <Spinner size="small" label={f.plLoading} />}
          {pl && (
            <>
              <div className={s.kpiRow}>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>{f.plRevenue}</div>
                  <div className={s.kpiValue} style={{ color: tokens.colorPaletteGreenForeground1 }}>{fmt(pl.totalRevenue)}</div>
                  <div className={s.sub}>{f.plRevenueSub}</div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>{f.plCosts}</div>
                  <div className={s.kpiValue} style={{ color: tokens.colorPaletteRedForeground1 }}>{fmt(pl.totalCosts)}</div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>{f.plMargin}</div>
                  <div className={s.kpiValue} style={{ color: pl.grossMargin >= 0 ? tokens.colorPaletteGreenForeground1 : tokens.colorPaletteRedForeground1 }}>
                    {fmt(pl.grossMargin)}
                  </div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>{f.plMarginPct}</div>
                  <div className={s.kpiValue} style={{ color: (pl.marginPct ?? 0) >= 0 ? tokens.colorPaletteGreenForeground1 : tokens.colorPaletteRedForeground1 }}>
                    {fmtPct(pl.marginPct)}
                  </div>
                </div>
              </div>

              <div className={s.grid}>
                <div className={s.card}>
                  <Text weight="semibold">{f.plCostByCategory}</Text>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={Object.entries(pl.costByCategory).map(([cat, val]) => ({ name: t.finance.categories[cat as keyof typeof t.finance.categories] ?? cat, cost: val }))}
                      layout="vertical"
                      margin={{ top: 4, right: 24, left: 80, bottom: 4 }}
                    >
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                      <Tooltip formatter={(v: number) => fmt(v)} />
                      <Bar dataKey="cost" fill={tokens.colorBrandBackground} name={f.costLabel} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className={s.card}>
                  <Text weight="semibold">{f.weeklyTrend}</Text>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={pl.weeklyTrend} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v: number) => fmt(v)} />
                      <Line type="monotone" dataKey="cost" stroke={tokens.colorBrandBackground} dot={false} name={f.costLabel} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ── TAB: CASHFLOW ───────────────────────────────────────────────────── */}
      {tab === 'cashflow' && (
        <>
          <div className={s.granularityRow}>
            <Text size={200}>{f.cashflowTitle}:</Text>
            {(['week', 'month'] as const).map((g) => (
              <button
                key={g}
                onClick={() => setCashGranularity(g)}
                style={{
                  padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px',
                  border: `1px solid ${tokens.colorNeutralStroke1}`,
                  background: cashGranularity === g ? tokens.colorBrandBackground : 'transparent',
                  color: cashGranularity === g ? tokens.colorNeutralForegroundInverted : tokens.colorNeutralForeground1,
                }}
              >
                {g === 'week' ? f.cashflowWeek : f.cashflowMonth}
              </button>
            ))}
          </div>

          {loadingCashflow && <Spinner size="small" label={f.cashflowLoading} />}

          {cashflow && (
            <div className={s.card}>
              <Text weight="semibold">{f.cashflowTitle}</Text>
              <ResponsiveContainer width="100%" height={340}>
                <AreaChart data={cashflow.cashflow} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => fmt(v)} />
                  <Legend />
                  <Area type="monotone" dataKey="inflow"  stackId="1" stroke="#27ae60" fill="#d5f5e3" name={f.cashflowInflow}  />
                  <Area type="monotone" dataKey="outflow" stackId="2" stroke="#c0392b" fill="#fadbd8" name={f.cashflowOutflow} />
                  <Line type="monotone" dataKey="balance" stroke={tokens.colorBrandForeground1} dot={false} strokeWidth={2} name={f.cashflowBalance} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}

      {/* ── TAB: PRESUPUESTO ────────────────────────────────────────────────── */}
      {tab === 'presupuesto' && (
        <>
          {isAdmin && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button onClick={() => setEditCommittedOpen(true)}>{f.editCommittedTitle}</Button>
              <Button appearance="primary" onClick={() => setNewBudgetOpen(true)}>{f.newBudgetBtn}</Button>
            </div>
          )}
          {loadingBudget && <Spinner size="small" label={f.budgetLoading} />}

          {budgetBreakdown && (
            <>
              <div className={s.kpiRow} style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>{f.budgetTotalPlanned}</div>
                  <div className={s.kpiValue}>{fmt(budgetBreakdown.totalPlanned)}</div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>{f.budgetTotalExecuted}</div>
                  <div className={s.kpiValue}>{fmt(budgetBreakdown.totalExecuted)}</div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>{f.budgetTotalCommitted}</div>
                  <div className={s.kpiValue}>{fmt(budgetBreakdown.totalCommitted)}</div>
                </div>
              </div>

              <div className={s.card}>
                <Text weight="semibold">{f.budgetTitle}</Text>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={budgetBreakdown.breakdown.map(r => ({
                      name:            t.finance.categories[r.category as keyof typeof t.finance.categories] ?? r.category,
                      Presupuestado:   r.planned,
                      Ejecutado:       r.executed,
                    }))}
                    margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v: number) => fmt(v)} />
                    <Legend />
                    <Bar dataKey="Presupuestado" fill={tokens.colorNeutralStroke1Hover} />
                    <Bar dataKey="Ejecutado"     fill={tokens.colorBrandBackground} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className={s.card}>
                <div className={`${s.budgetRow} ${s.protocolHeader}`}>
                  <span>{f.budgetCategory}</span>
                  <span style={{ textAlign: 'right' }}>{f.budgetPlanned}</span>
                  <span style={{ textAlign: 'right' }}>{f.budgetExecuted}</span>
                  <span style={{ textAlign: 'right' }}>{f.budgetVariance}</span>
                  <span style={{ textAlign: 'right' }}>{f.budgetPct}</span>
                </div>
                {budgetBreakdown.breakdown.map((row: BudgetBreakdownRow) => (
                  <div key={row.category} className={s.budgetRow}>
                    <span>{t.finance.categories[row.category as keyof typeof t.finance.categories] ?? row.category}</span>
                    <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(row.planned)}</span>
                    <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(row.executed)}</span>
                    <span className={row.variance >= 0 ? s.positive : s.negative} style={{ textAlign: 'right' }}>
                      {row.variance >= 0 ? '+' : ''}{fmt(row.variance)}
                    </span>
                    <span
                      style={{ textAlign: 'right' }}
                      className={row.pct == null ? '' : row.pct > 100 ? s.negative : row.pct > 80 ? '' : s.positive}
                    >
                      {fmtPct(row.pct)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      <NewBudgetDialog open={newBudgetOpen} onClose={() => setNewBudgetOpen(false)} />
      <EditCommittedDialog open={editCommittedOpen} onClose={() => setEditCommittedOpen(false)} />
    </div>

  );
}
